<script lang="ts">
	import { onMount } from 'svelte';
	import { page } from '$app/stores';
	import { theme } from '$lib/stores/theme';
	import { locale } from 'svelte-i18n';
	import { scrollPositionManager } from '$lib/stores/scrollPosition';
	import { errorTracker } from '$lib/utils/errorTracking';
	import { initTheme } from '$lib/utils/theme';
	import { setLoading } from '$lib/stores/loading';
	import CookieBanner from '$lib/components/ui/CookieBanner.svelte';
	import '../app.css';

	let { children } = $props();

	// These routes set their own (often query-param-aware) canonical in their +page.svelte.
	// For every other page we emit a self-referencing canonical here so Google always
	// knows the master URL — without creating duplicate canonical tags on the routes above.
	const SELF_CANONICAL = new Set([
		'/', '/blog', '/blog/[slug]', '/changelog', '/changelog/[slug]', '/contact',
		'/docs/[...slug]', '/faq', '/press', '/press-kit', '/search', '/stories',
		'/stories/[slug]', '/weekly/author/[author]', '/weekly/blog/[slug]'
	]);

	// Routes that already set their own og:type/og:url (some use og:type=article).
	// Every other page gets a default og:type=website + og:url here, so social/answer
	// engines get complete Open Graph tags without duplicating on the routes above.
	const SELF_OG = new Set([
		'/', '/blog', '/blog/[slug]', '/changelog', '/changelog/[slug]',
		'/docs/[...slug]', '/guides/[slug]', '/press', '/search', '/stories'
	]);

	onMount(() => {
		theme.initialize();
		// Initialize the new theme system
		initTheme();
		// Scroll position manager initializes automatically
		// Error tracking initializes automatically on import
		
		// Mark translations as loaded since they're loaded in +layout.ts
		setLoading('translations', false);
	});
	
	// Update HTML lang attribute whenever locale changes
	$effect(() => {
		if ($locale) {
			document.documentElement.setAttribute('lang', $locale.split('-')[0]);
		}
	});
</script>

<svelte:head>
	{#if $page.route.id && !SELF_CANONICAL.has($page.route.id)}
		<link rel="canonical" href="https://heyweek.com{$page.url.pathname}" />
	{/if}
	{#if $page.route.id && !SELF_OG.has($page.route.id)}
		<meta property="og:type" content="website" />
		<meta property="og:url" content="https://heyweek.com{$page.url.pathname}" />
	{/if}

	<!-- Organization entity, emitted site-wide so every page reinforces the same
	     brand identity for search + AI engines. Rendered via {@html} so the JSON
	     is evaluated, not printed as text. -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Heyweek',
		url: 'https://heyweek.com',
		logo: 'https://heyweek.com/heyweek-logo.svg',
		description:
			'Heyweek is all-in-one professional services automation (PSA) software that lets freelancers and growing teams run their entire business — time, projects, clients, invoicing, and finances — from one connected workspace.',
		sameAs: ['https://twitter.com/heyweekapp', 'https://www.linkedin.com/company/heyweek/']
	}).replace(/</g, '\\u003c')}</` + `script>`}
</svelte:head>

{@render children?.()}
<CookieBanner />